<template>
  <div class="location-map-selector">
    <!-- Map Container -->
    <div id="location-map" class="map-container"></div>
    
    <!-- Location Info Panel -->
    <div class="location-info-panel">
      <div class="coordinates-display">
        <div class="coordinate-item">
          <label>Latitude:</label>
          <span>{{ selectedLocation.latitude.toFixed(6) }}</span>
        </div>
        <div class="coordinate-item">
          <label>Longitude:</label>
          <span>{{ selectedLocation.longitude.toFixed(6) }}</span>
        </div>
        <div class="coordinate-item">
          <label>Accuracy:</label>
          <span>{{ selectedLocation.accuracy ? selectedLocation.accuracy.toFixed(1) + 'm' : 'N/A' }}</span>
        </div>
      </div>
      
      <div class="action-buttons">
        <button 
          class="btn btn-secondary" 
          @click="closeMap"
        >
          Cancel
        </button>
        <button 
          class="btn btn-primary" 
          @click="confirmLocation"
          :disabled="!selectedLocation.latitude"
        >
          Confirm Location
        </button>
      </div>
    </div>
    
    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner">
        <i class="fas fa-spinner fa-spin"></i>
        <span>Loading map...</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LocationMapSelector',
  props: {
    initialLocation: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      map: null,
      marker: null,
      selectedLocation: {
        latitude: 0,
        longitude: 0,
        accuracy: null,
        timestamp: null
      },
      isLoading: true,
      mapInitialized: false
    }
  },
  mounted() {
    this.initializeMap();
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    async initializeMap() {
      try {
        this.isLoading = true;
        
        // Load Leaflet CSS dynamically
        if (!document.querySelector('#leaflet-css')) {
          const link = document.createElement('link');
          link.id = 'leaflet-css';
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
        }
        
        // Load Leaflet JS dynamically
        if (typeof L === 'undefined') {
          await this.loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js');
        }
        
        // Initialize map
        this.map = L.map('location-map').setView([0, 0], 13);
        
        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(this.map);
        
        // Get current location and set map view
        await this.getCurrentLocation();
        
        this.mapInitialized = true;
        this.isLoading = false;
        
      } catch (error) {
        console.error('Error initializing map:', error);
        this.isLoading = false;
        this.$toast.error('Failed to load map. Please try again.');
      }
    },
    
    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    
    async getCurrentLocation() {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          });
        });
        
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        };
        
        this.selectedLocation = coords;
        
        // Set map view to current location
        this.map.setView([coords.latitude, coords.longitude], 15);
        
        // Add marker for current location
        this.addMarker(coords.latitude, coords.longitude, 'Current Location');
        
        // Add click event to map for location selection
        this.map.on('click', (e) => {
          this.onMapClick(e);
        });
        
      } catch (error) {
        console.error('Error getting current location:', error);
        
        // Set default view (can be customized)
        this.map.setView([0, 0], 2);
        
        // Add click event to map for manual location selection
        this.map.on('click', (e) => {
          this.onMapClick(e);
        });
        
        this.$toast.warning('Could not get current location. You can click on the map to select a location.');
      }
    },
    
    addMarker(lat, lng, title) {
      // Remove existing marker
      if (this.marker) {
        this.map.removeLayer(this.marker);
      }
      
      // Add new marker
      this.marker = L.marker([lat, lng]).addTo(this.map);
      this.marker.bindPopup(title || 'Selected Location').openPopup();
    },
    
    onMapClick(e) {
      const { lat, lng } = e.latlng;
      
      // Update selected location
      this.selectedLocation = {
        latitude: lat,
        longitude: lng,
        accuracy: null,
        timestamp: Date.now()
      };
      
      // Update marker
      this.addMarker(lat, lng, 'Selected Location');
      
      // Show feedback
      this.$toast.info('Location selected! Click "Confirm Location" to use this position.');
    },
    
    confirmLocation() {
      // Emit the selected location to parent component
      this.$emit('location-selected', {
        ...this.selectedLocation,
        coordinates: `${this.selectedLocation.latitude}, ${this.selectedLocation.longitude}`
      });
      
      this.closeMap();
    },
    
    closeMap() {
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
.location-map-selector {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 500px;
}

.map-container {
  width: 100%;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.location-info-panel {
  margin-top: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.coordinates-display {
  margin-bottom: 20px;
}

.coordinate-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #dee2e6;
}

.coordinate-item:last-child {
  border-bottom: none;
}

.coordinate-item label {
  font-weight: 600;
  color: #495057;
  min-width: 80px;
}

.coordinate-item span {
  font-family: 'Courier New', monospace;
  color: #6c757d;
  background: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #ced4da;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #007bff;
  font-size: 16px;
}

.loading-spinner i {
  font-size: 24px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .map-container {
    height: 300px;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
}
</style>
